import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';

const NOTICE_LABELS = { myvip: 'My Community VIP', rpp: 'Resident Parking Permit', r2park: 'Register2Park' };
const NOTICE_COLORS = { myvip: '#8b5cf6', rpp: '#3b82f6', r2park: '#10b981' };
const STATUS_COLORS = { active: '#22c55e', superseded: '#f59e0b', void: '#6b7280' };
const VERSION_LABELS = { 1: '1st', 2: '2nd', 3: '3rd' };

function dateToCT(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

function getTodayCT() { return dateToCT(new Date()); }
function getLast7DaysCT() { return dateToCT(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)); }
function getThisMonthStartCT() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit' }).formatToParts(now);
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}-01`;
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

export default function NoticeHistoryPage() {
  const navigate = useNavigate();
  const [activePreset, setActivePreset] = useState('month');
  const [filters, setFilters] = useState({
    date_from: getThisMonthStartCT(),
    date_to: getTodayCT(),
    notice_type: '',
    search: '',
  });
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);

  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('');

  function handleSort(col) {
    if (sortBy !== col) { setSortBy(col); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else { setSortBy(''); setSortDir(''); }
  }
  function sortArrow(col) { if (sortBy !== col) return ''; return sortDir === 'asc' ? ' \u25B2' : ' \u25BC'; }
  const thStyle = { cursor: 'pointer', userSelect: 'none' };

  const buildFilters = useCallback(() => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.notice_type) params.notice_type = filters.notice_type;
    if (filters.search) params.search = filters.search;
    return params;
  }, [filters]);

  const doSearch = useCallback(async (pageNum, searchFilters, sb, sd) => {
    setLoading(true);
    setError('');
    try {
      const queryFilters = { ...searchFilters, page: pageNum, page_size: 50 };
      if (sb) { queryFilters.sort_by = sb; queryFilters.sort_dir = sd; }
      const data = await api.getNoticeHistory(queryFilters);
      if (data && data.pagination) {
        setResults(data.data);
        setPagination(data.pagination);
        setPage(data.pagination.page);
      } else {
        setResults(Array.isArray(data) ? data : []);
        setPagination(null);
      }
      setSearched(true);
      api.getNoticeHistoryStats(searchFilters).then(setStats).catch(() => {});
    } catch (err) {
      setError(err.message || 'Search failed');
    }
    setLoading(false);
  }, []);

  // Auto-search on load (this month)
  useEffect(() => {
    doSearch(1, { date_from: getThisMonthStartCT(), date_to: getTodayCT() }, '', '');
  }, []); // eslint-disable-line

  // Re-search on sort change
  useEffect(() => {
    if (searched) doSearch(1, buildFilters(), sortBy, sortDir);
  }, [sortBy, sortDir]); // eslint-disable-line

  function handlePreset(preset) {
    setActivePreset(preset);
    if (preset === 'today') {
      const today = getTodayCT();
      const f = { ...filters, date_from: today, date_to: today };
      setFilters(f); setPage(1); doSearch(1, f, sortBy, sortDir);
    } else if (preset === '7days') {
      const f = { ...filters, date_from: getLast7DaysCT(), date_to: getTodayCT() };
      setFilters(f); setPage(1); doSearch(1, f, sortBy, sortDir);
    } else if (preset === 'month') {
      const f = { ...filters, date_from: getThisMonthStartCT(), date_to: getTodayCT() };
      setFilters(f); setPage(1); doSearch(1, f, sortBy, sortDir);
    } else if (preset === 'custom') {
      setFilters(f => ({ ...f, date_from: '', date_to: '' }));
    }
  }

  function handleSearchClick() { setPage(1); doSearch(1, buildFilters(), sortBy, sortDir); }

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams(buildFilters()).toString();
      const response = await fetch(`/api/notices/history/export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notice-history-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export');
    }
    setExporting(false);
  }

  async function downloadFile(id, format, notice) {
    try {
      const response = await fetch(`/api/notices/${id}/download/${format}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = NOTICE_LABELS[notice.notice_type] || notice.notice_type;
      a.download = `${notice.property_name} - ${label} - v${notice.version}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download file');
    }
  }

  const hasActiveFilters = filters.notice_type || filters.search;

  function clearFilters() {
    const cleared = { ...filters, notice_type: '', search: '' };
    setFilters(cleared);
    doSearch(1, cleared, sortBy, sortDir);
  }

  const statsCards = stats ? [
    { label: 'Total Notices', value: stats.total || 0, color: '#0f172a' },
    { label: 'MyVIP', value: stats.myvip_count || 0, color: '#8b5cf6' },
    { label: 'RPP', value: stats.rpp_count || 0, color: '#3b82f6' },
    { label: 'R2Park', value: stats.r2park_count || 0, color: '#10b981' },
    { label: 'Properties', value: stats.unique_properties || 0, color: '#f59e0b' },
  ] : [];

  return (
    <div className="admin-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Notice History</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => navigate('/sales/notices')}>
            Generate Notice
          </button>
          <button className="btn btn-gold" onClick={handleExport} disabled={exporting || !searched || results.length === 0}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
          {statsCards.map(s => (
            <div key={s.label} style={{
              background: '#fff', borderRadius: 12, padding: '14px 16px',
              border: '1px solid #e2e8f0', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="search-filters">
        <div className="date-preset-row">
          {['today', '7days', 'month', 'custom'].map(p => (
            <button key={p} type="button" className={`btn btn-sm ${activePreset === p ? 'btn-primary' : 'btn-muted'}`}
              onClick={() => handlePreset(p)}>
              {p === 'today' ? 'Today' : p === '7days' ? 'Last 7 Days' : p === 'month' ? 'This Month' : 'Custom'}
            </button>
          ))}
        </div>

        {activePreset === 'custom' && (
          <div className="date-range-row">
            <label>From</label>
            <input type="date" className="form-input" value={filters.date_from}
              onChange={e => setFilters({ ...filters, date_from: e.target.value })} />
            <label>To</label>
            <input type="date" className="form-input" value={filters.date_to}
              onChange={e => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
        )}

        <div className="filter-row">
          <input placeholder="Search property name or address..." value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            style={{ minWidth: 220 }} />
          <div className="form-group">
            <select className="form-input" value={filters.notice_type}
              onChange={e => setFilters({ ...filters, notice_type: e.target.value })}>
              <option value="">All Types</option>
              <option value="myvip">My Community VIP</option>
              <option value="rpp">Resident Parking Permit</option>
              <option value="r2park">Register2Park</option>
            </select>
          </div>
          <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSearchClick}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters}
              style={{ fontSize: 11, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee', border: '1px solid #c00', borderRadius: 8, color: '#900', marginBottom: 16, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {searched && (
        <div className="history-results">
          <p className="text-muted">
            {pagination
              ? `${pagination.total} result${pagination.total !== 1 ? 's' : ''} \u2014 Page ${pagination.page} of ${pagination.total_pages}`
              : `${results.length} result${results.length !== 1 ? 's' : ''}`
            }
          </p>
          {results.length === 0 ? (
            <div className="empty-state">No notices found matching your criteria.</div>
          ) : (
            <>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={thStyle} onClick={() => handleSort('property_name')}>Property{sortArrow('property_name')}</th>
                      <th style={thStyle} onClick={() => handleSort('notice_type')}>Type{sortArrow('notice_type')}</th>
                      <th style={thStyle} onClick={() => handleSort('version')}>Version{sortArrow('version')}</th>
                      <th style={thStyle} onClick={() => handleSort('status')}>Status{sortArrow('status')}</th>
                      <th style={thStyle} onClick={() => handleSort('created_by_name')}>Created By{sortArrow('created_by_name')}</th>
                      <th style={thStyle} onClick={() => handleSort('created_at')}>Created{sortArrow('created_at')}</th>
                      <th>Downloads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(r => (
                      <tr key={r.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{r.property_name}</div>
                          {r.property_address && <div style={{ fontSize: 11, color: '#64748b' }}>{r.property_address}</div>}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                            fontSize: 11, fontWeight: 700,
                            background: `${NOTICE_COLORS[r.notice_type] || '#94a3b8'}18`,
                            color: NOTICE_COLORS[r.notice_type] || '#94a3b8',
                          }}>
                            {NOTICE_LABELS[r.notice_type] || r.notice_type}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{VERSION_LABELS[r.version] || r.version}</td>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                            fontSize: 11, fontWeight: 700,
                            background: `${STATUS_COLORS[r.status] || '#94a3b8'}18`,
                            color: STATUS_COLORS[r.status] || '#94a3b8',
                            textTransform: 'capitalize',
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td>{r.created_by_name || '\u2014'}</td>
                        <td>{formatDateDisplay(r.created_at)}</td>
                        <td>
                          {r.docx_path && (
                            <button onClick={() => downloadFile(r.id, 'docx', r)}
                              style={{ fontSize: 11, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, marginRight: 8 }}>
                              DOCX
                            </button>
                          )}
                          {r.pdf_path && (
                            <button onClick={() => downloadFile(r.id, 'pdf', r)}
                              style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                              PDF
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination && pagination.total_pages > 1 && (
                <div className="pagination-controls">
                  <button type="button" className="btn btn-muted btn-sm"
                    onClick={() => doSearch(page - 1, buildFilters(), sortBy, sortDir)} disabled={page <= 1 || loading}>
                    Previous
                  </button>
                  <span className="pagination-info">Page {pagination.page} of {pagination.total_pages}</span>
                  <button type="button" className="btn btn-muted btn-sm"
                    onClick={() => doSearch(page + 1, buildFilters(), sortBy, sortDir)} disabled={page >= pagination.total_pages || loading}>
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
